import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Companies from "@/pages/Companies";
import CompanyDetail from "@/pages/CompanyDetail";
import RunPayroll from "@/pages/RunPayroll";
import PayrollHistory from "@/pages/PayrollHistory";
import RunDetail from "@/pages/RunDetail";
import Remittance from "@/pages/Remittance";
import Notifications from "@/pages/Notifications";
import Reports from "@/pages/Reports";
import TrustBalance from "@/pages/TrustBalance";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import T4Slips from "@/pages/T4Slips";
import ROEWorkspace from "@/pages/ROEWorkspace";
import Settings from "@/pages/Settings";
import Staff from "@/pages/Staff";
import SetPassword from "@/pages/SetPassword";
import MagicLink from "@/pages/MagicLink";
import EmployeeLogin from "@/pages/employee/EmployeeLogin";
import EmployeeRegister from "@/pages/employee/EmployeeRegister";
import EmployeeLayout from "@/components/EmployeeLayout";
import MeHome from "@/pages/employee/MeHome";
import MePay from "@/pages/employee/MePay";
import MeTime from "@/pages/employee/MeTime";
import MeDocs from "@/pages/employee/MeDocs";
import MeProfile from "@/pages/employee/MeProfile";
import MeWelcome from "@/pages/employee/MeWelcome";
import Onboarding from "@/pages/Onboarding";
import Time from "@/pages/Time";
import People from "@/pages/People";
import Migration from "@/pages/Migration";
import ClientLayout from "@/components/ClientLayout";
import ClientDashboard from "@/pages/ClientDashboard";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafafa]">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground animate-pulse">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "employee") return <Navigate to="/me" replace />;
  if (user.role === "client") return <Navigate to="/client" replace />;
  return <Layout>{children}</Layout>;
}

function EmployeeProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center bg-[#f4f4f2]"><div className="font-mono text-xs uppercase tracking-widest text-neutral-400 animate-pulse">Loading…</div></div>;
  if (!user) return <Navigate to="/employee/login" replace />;
  if (user.role !== "employee" && !(user.role === "client" && user.linked_employee_id)) return <Navigate to={user.role === "client" ? "/client" : "/dashboard"} replace />;
  return children;
}

function ClientProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sky-50">
        <div className="font-mono text-xs uppercase tracking-widest text-slate-400 animate-pulse">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "employee") return <Navigate to="/me" replace />;
  if (user.role !== "client") return <Navigate to="/dashboard" replace />;
  return <ClientLayout>{children}</ClientLayout>;
}

function AppShell() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/employee/login" element={<EmployeeLogin />} />
      <Route path="/employee/register" element={<EmployeeRegister />} />
      <Route path="/me" element={<EmployeeProtected><EmployeeLayout /></EmployeeProtected>}>
        <Route index element={<MeHome />} />
        <Route path="pay" element={<MePay />} />
        <Route path="time" element={<MeTime />} />
        <Route path="documents" element={<MeDocs />} />
        <Route path="profile" element={<MeProfile />} />
        <Route path="welcome" element={<MeWelcome />} />
      </Route>
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/magic" element={<MagicLink />} />
      <Route path="/onboard/:token" element={<Onboarding />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/companies" element={<Protected><Companies /></Protected>} />
      <Route path="/companies/:companyId" element={<Protected><CompanyDetail /></Protected>} />
      <Route path="/run-payroll" element={<Protected><RunPayroll /></Protected>} />
      <Route path="/time" element={<Protected><Time /></Protected>} />
      <Route path="/people" element={<Protected><People /></Protected>} />
      <Route path="/migration" element={<Protected><Migration /></Protected>} />
      <Route path="/history" element={<Protected><PayrollHistory /></Protected>} />
      <Route path="/history/:runId" element={<Protected><RunDetail /></Protected>} />
      <Route path="/remittance" element={<Protected><Remittance /></Protected>} />
      <Route path="/notifications" element={<Protected><Navigate to={`/settings?tab=notifications${window.location.search ? "&" + window.location.search.slice(1) : ""}`} replace /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/reports/trust-balance" element={<Protected><TrustBalance /></Protected>} />
      <Route path="/t4" element={<Protected><T4Slips /></Protected>} />
      <Route path="/roe" element={<Protected><ROEWorkspace /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/staff" element={<Protected><Staff /></Protected>} />

      {/* Client portal */}
      <Route path="/client" element={<ClientProtected><ClientDashboard /></ClientProtected>} />
      <Route path="/client/employees" element={<ClientProtected><CompanyDetail /></ClientProtected>} />
      <Route path="/client/company" element={<ClientProtected><CompanyDetail /></ClientProtected>} />
      <Route path="/client/time" element={<ClientProtected><Time /></ClientProtected>} />
      <Route path="/client/people" element={<ClientProtected><People /></ClientProtected>} />
      <Route path="/client/migration" element={<ClientProtected><Migration /></ClientProtected>} />
      <Route path="/client/run-payroll" element={<ClientProtected><RunPayroll /></ClientProtected>} />
      <Route path="/client/history" element={<ClientProtected><PayrollHistory /></ClientProtected>} />
      <Route path="/client/history/:runId" element={<ClientProtected><RunDetail /></ClientProtected>} />
      <Route path="/client/reports" element={<ClientProtected><Reports /></ClientProtected>} />
      <Route path="/client/t4" element={<ClientProtected><T4Slips /></ClientProtected>} />
      <Route path="/client/roe" element={<ClientProtected><ROEWorkspace /></ClientProtected>} />
      <Route path="/client/remittance" element={<ClientProtected><Remittance /></ClientProtected>} />
      <Route path="/client/notifications" element={<ClientProtected><Notifications /></ClientProtected>} />

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ErrorBoundary>
            <AppShell />
          </ErrorBoundary>
          <Toaster position="top-right" toastOptions={{ style: { borderRadius: 0, border: "1px solid #0a0a0a" } }} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
